import axios, { AxiosRequestConfig } from 'axios';
import { ApiContext } from 'api/api-context';
import { ActionName, actions, GetActionMap } from 'api/actions/actions';
import useLocalStorage from 'hooks/use-local-storage';
import { REST_API_TIMEOUT, REST_API_URL } from 'env';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useLang from 'lang';
import { useSearchParams } from 'react-router-dom';

/**
 * The session provider is used to provide the session context.
 */
export default function ApiProvider({ children }: { children: React.ReactNode }) {
  const { lang } = useLang();
  const [searchParams, setParams] = useSearchParams();
  const [ready, setReady] = useState(false);
  const jwtFromUrl = searchParams.get('jwt') || '';

  const [{ jwt: storedJwt }, setJwt] = useLocalStorage<{ jwt: string }>('phillip.session.jwt', {
    jwt: '',
  });
  const jwt = jwtFromUrl || storedJwt;

  const [{ buildingId }, setBuildingId] = useLocalStorage<{ buildingId: number | null }>('phillip.session.buildingId', {
    buildingId: searchParams.get('buildingId') ? Number(searchParams.get('buildingId')) : null,
  });

  const [{ logoUrl }, setLogoUrl] = useLocalStorage<{ logoUrl: string | null }>('phillip.session.logoUrl', {
    logoUrl: null,
  });

  const [{ buildingName }, setBuildingName] = useLocalStorage<{ buildingName: string | null }>(
    'phillip.session.buildingName',
    {
      buildingName: null,
    }
  );

  /**
   * Destroys the session.
   */
  const destroySession = useCallback(() => {
    setJwt({ jwt: '' });
    // window.open(`${LOGIN_PAGE_PATH.original}?logout=true`, '_self');
  }, []);

  const setPublicBranding = useCallback(
    (branding: { buildingId: number; logoUrl: string | null; buildingName: string | null }) => {
      setBuildingId({ buildingId: branding.buildingId });
      setLogoUrl({ logoUrl: branding.logoUrl });
      setBuildingName({ buildingName: branding.buildingName });
    },
    [setBuildingId, setLogoUrl, setBuildingName]
  );

  /**
   * Creates a contextualized action.
   */
  const getAction: GetActionMap = useMemo(() => {
    const connector = axios.create({
      timeout: REST_API_TIMEOUT,
      baseURL: REST_API_URL,
    });

    /**
     * Adds JWT to the request.
     */
    connector.interceptors.request.use((cfg) => {
      if (jwt) {
        cfg.headers.Authorization = `Bearer ${jwt}`;
      }

      cfg.headers['Accept-Language'] = lang;

      return cfg;
    });

    connector.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response?.status === 401) {
          destroySession();
        }

        return Promise.reject(error);
      }
    );

    const contextualizedActions = new Map(
      Array.from(actions.entries()).map(([name, createAction]) => [name, createAction(connector)])
    );

    return (action: ActionName) => contextualizedActions.get(action) as any;
  }, [jwt, lang]);

  const request = useCallback(
    async <T,>(config: AxiosRequestConfig) => {
      try {
        const response = await axios.request<T>({
          ...config,
          baseURL: REST_API_URL,
          timeout: REST_API_TIMEOUT,
          headers: {
            ...(config.headers || {}),
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
            'Accept-Language': lang,
          },
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 401) destroySession();
        throw error;
      }
    },
    [jwt, lang, destroySession]
  );

  // Check if there is a JWT or buildingId in the URL.
  useEffect(() => {
    const buildingIdFromUrl = searchParams.get('buildingId');

    // URL sessions are used by admin deep links and the native booking login.
    // Persist the token before removing it from the URL so it survives the next navigation.
    if (jwtFromUrl) {
      setJwt({ jwt: jwtFromUrl });
    }

    // If there's a buildingId in URL, validate it against user's accessible buildings
    if (buildingIdFromUrl && jwt) {
      const getCurrentUser = getAction('GetCurrentUser');
      getCurrentUser({})
        .then((response) => {
          const userBuildings = response.buildings || [];
          const requestedBuildingId = Number(buildingIdFromUrl);
          const hasAccess = userBuildings.some((b) => b.id === requestedBuildingId);

          if (hasAccess) {
            setBuildingId({ buildingId: requestedBuildingId });
            const building = userBuildings.find((b) => b.id === requestedBuildingId);
            setLogoUrl({ logoUrl: building?.logoUrl ?? null });
            setBuildingName({ buildingName: building?.title ?? null });
          } else {
            // User doesn't have access - redirect to error page
            window.location.href = '/403'; // or create a custom error page
          }

          // Remove buildingId from URL
          setParams(
            (prev) => {
              prev.delete('buildingId');
              prev.delete('jwt');
              return prev;
            },
            { replace: true }
          );
          setReady(true);
        })
        .catch(() => {
          // API error - still remove params and set ready
          setParams(
            (prev) => {
              prev.delete('buildingId');
              prev.delete('jwt');
              return prev;
            },
            { replace: true }
          );
          setReady(true);
        });
    } else {
      // No buildingId in URL - just handle JWT
      setParams(
        (prev) => {
          prev.delete('jwt');
          return prev;
        },
        { replace: true }
      );
      setReady(true);
    }
  }, []);

  const value = useMemo(
    () => ({ jwt, buildingId, logoUrl, buildingName, setPublicBranding, getAction, request, destroySession }),
    [jwt, buildingId, logoUrl, buildingName, setPublicBranding, getAction, request, destroySession]
  );

  return <ApiContext.Provider value={value}>{ready ? children : <></>}</ApiContext.Provider>;
}
